import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const form = e.currentTarget;
      const formDataToSend = new FormData(form);
      
      const response = await fetch('https://readdy.ai/api/form/d6g61171jbcgr9kd7af0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/80 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/f431212c-6c1f-4d7a-b6a6-c261b29799bf.png" 
              alt="PRERO d.o.o. Logo" 
              className="h-12 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-24 overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-[55%] space-y-8">
              <div className="inline-block px-6 py-2 bg-purple-100 rounded-full">
                <span className="text-purple-700 font-medium text-sm tracking-wide">iOS Development Experts</span>
              </div>
              
              <h1 className="text-7xl font-extrabold leading-tight">
                Modern Applications for <span className="bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">iOS</span> Platform
              </h1>
              
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed max-w-xl">
                <p>
                  PRERO d.o.o. is a team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.
                </p>
                <p>
                  We combine cutting-edge technology with intuitive design to create applications that users love and trust. Our commitment to excellence drives everything we do.
                </p>
              </div>
            </div>

            <div className="lg:w-[45%] relative">
              <div className="relative z-10">
                <img 
                  src="https://public.readdy.ai/ai/img_res/bf91e3c1e6662b4dbcb72f302ae5b902.jpg" 
                  alt="iOS Application" 
                  className="w-full h-auto object-contain drop-shadow-2xl"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-purple-400/30 via-pink-400/30 to-purple-600/30 blur-3xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-16 ml-auto max-w-xl">
              <p className="text-gray-400 text-xs uppercase tracking-widest mb-4">WHO WE ARE</p>
              <h2 className="text-5xl font-extrabold leading-tight text-gray-900">
                A Team Dedicated to <span className="font-extrabold">iOS Excellence</span>
              </h2>
            </div>

            <div className="ml-auto max-w-xl space-y-8 mb-16">
              <p className="text-gray-600 text-lg leading-relaxed">
                At PRERO d.o.o., we specialize in creating innovative iOS applications that stand out in the App Store. Our expertise spans from concept to deployment, ensuring every detail meets the highest standards.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With years of experience in mobile development, our team understands what it takes to build applications that not only function flawlessly but also delight users with exceptional design and performance.
              </p>
            </div>

            <div className="flex gap-6">
              <div className="w-[40%] h-80 rounded-3xl overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20software%20development%20team%20collaborating%20on%20iOS%20mobile%20app%20project%2C%20modern%20office%20workspace%2C%20developers%20working%20together%2C%20MacBooks%20and%20iPhones%2C%20bright%20natural%20lighting%2C%20clean%20minimalist%20environment%2C%20teamwork%20atmosphere&width=500&height=600&seq=about-team-001&orientation=portrait" 
                  alt="Team Collaboration" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-[60%] h-80 rounded-3xl overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20minimalist%20tech%20office%20interior%20with%20large%20windows%2C%20clean%20white%20workspace%2C%20Apple%20devices%2C%20contemporary%20furniture%2C%20natural%20daylight%2C%20professional%20software%20company%20environment%2C%20sleek%20design%2C%20organized%20space&width=700&height=600&seq=about-office-001&orientation=landscape" 
                  alt="Modern Office" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-[#F8F6F3]">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <p className="text-gray-400 text-xs uppercase tracking-widest mb-4">OUR PRODUCTS</p>
              <h2 className="text-6xl font-extrabold text-gray-900">VPN & Cleaner</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* VPN Card */}
              <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <div className="h-52 bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                  <div className="w-20 h-20 flex items-center justify-center">
                    <i className="ri-shield-line text-white text-7xl"></i>
                  </div>
                </div>
                <div className="p-8 space-y-6">
                  <h3 className="text-3xl font-bold text-gray-900">VPN</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Secure your online presence with our advanced VPN solution. Protect your privacy, encrypt your data, and browse the internet with complete peace of mind on your iOS device.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium whitespace-nowrap">Military-grade Encryption</span>
                    <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium whitespace-nowrap">No-logs Policy</span>
                    <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium whitespace-nowrap">Fast Servers</span>
                  </div>
                </div>
              </div>

              {/* Cleaner Card */}
              <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                <div className="h-52 bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center">
                  <div className="w-20 h-20 flex items-center justify-center">
                    <i className="ri-brush-line text-white text-7xl"></i>
                  </div>
                </div>
                <div className="p-8 space-y-6">
                  <h3 className="text-3xl font-bold text-gray-900">Cleaner</h3>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Optimize your iOS device performance with our intelligent cleaner. Remove junk files, manage storage efficiently, and keep your device running smoothly at all times.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium whitespace-nowrap">Smart Cleaning</span>
                    <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium whitespace-nowrap">Storage Manager</span>
                    <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium whitespace-nowrap">Battery Optimizer</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-20 max-w-2xl">
              <p className="text-gray-400 text-xs uppercase tracking-widest mb-4">WHAT WE OFFER</p>
              <h2 className="text-6xl font-extrabold leading-tight text-gray-900">
                Enhanced Experience & Security
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-7">
              {/* Feature 1 */}
              <div className="bg-[#F5F5F5] rounded-2xl p-10 space-y-6">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-shield-check-line text-3xl text-gray-900"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Advanced Security</h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  Protect your digital life with enterprise-level security features. Our applications use the latest encryption standards to keep your data safe from threats.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-[#F5F5F5] rounded-2xl p-10 space-y-6">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-speed-line text-3xl text-gray-900"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Peak Performance</h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  Experience lightning-fast performance optimized for iOS. Our apps are designed to run smoothly without draining your battery or consuming excessive resources.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-[#F5F5F5] rounded-2xl p-10 space-y-6">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-lock-line text-3xl text-gray-900"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Privacy First</h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  Your privacy is our priority. We never collect unnecessary data, and we give you full control over your information with transparent privacy policies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#1A1A1A] mx-10 rounded-t-[32px] mt-24">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-12 gap-12">
              {/* Company Info */}
              <div className="md:col-span-4 space-y-8">
                <h2 className="text-4xl font-bold text-white">PRERO d.o.o.</h2>
                <p className="text-gray-400 text-base leading-relaxed">
                  Creating innovative iOS applications that enhance user experience and improve mobile device security.
                </p>
              </div>

              {/* Address */}
              <div className="md:col-span-3 space-y-5">
                <h3 className="text-xl font-medium text-white">Location</h3>
                <div className="space-y-2 text-gray-400 text-base">
                  <p><span className="text-gray-500">Address:</span> Trg Slobode 7</p>
                  <p><span className="text-gray-500">City:</span> Koprivnica</p>
                  <p><span className="text-gray-500">Zip:</span> 48000</p>
                  <p><span className="text-gray-500">Country:</span> Croatia</p>
                </div>
              </div>

              {/* Contact Details */}
              <div className="md:col-span-3 space-y-5">
                <h3 className="text-xl font-medium text-white">Contact</h3>
                <div className="space-y-3 text-gray-400 text-base">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line text-base"></i>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Email:</p>
                      <p>info@prerodoo.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-phone-line text-base"></i>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Number:</p>
                      <p>+1 3092478680</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="mt-20 max-w-2xl">
              <h3 className="text-2xl font-bold text-white mb-8">Get in Touch</h3>
              <form 
                id="contact_form" 
                data-readdy-form 
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    required
                    className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your Message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-6 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors resize-none"
                  ></textarea>
                  <p className="text-gray-500 text-sm mt-2">{formData.message.length}/500 characters</p>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? 'Sending...' : formStatus === 'success' ? 'Message Sent!' : formStatus === 'error' ? 'Error. Try Again' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Watermark */}
            <div className="mt-24 text-center">
              <p className="text-[#2A2A2A] text-8xl font-extrabold opacity-50">PRERO</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
